import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'
import { AiForecast, AnyForecast, LiveWind } from '../../backend/src/gusty'
import * as api from './api'
import { Area } from './components/App'

export interface SpotWithData {
  title: string
  spotId: number
  forecastSpotId?: number
  liveWindImageUrl?: string
  webcamUrl?: string
  offshore?: { start: number; end: number }
  liveWind: LiveWind
}

export interface GustyState {
  token: string | null
  lastUpdateTime: string | null
  loadCount: number
  isLoadingSpotsWithData: boolean
  spotsWithData: SpotWithData[] | null
  forecasts: AnyForecast[] | null
  isLoadingForecasts: boolean
  isLoadingAiForecast: boolean
  aiForecast: AiForecast | null
}

export const defaultState: GustyState = {
  token: localStorage.getItem('token'),
  lastUpdateTime: null,
  loadCount: 0,
  isLoadingSpotsWithData: false,
  spotsWithData: null,
  forecasts: null,
  isLoadingForecasts: false,
  aiForecast: null,
  isLoadingAiForecast: false,
}

declare module 'react-redux' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultRootState extends GustyState {}
}

export const loadForecasts = createAsyncThunk(
  'forecasts',
  async ({ forecastId, token }: { forecastId: string; token: string }) => {
    return api.getForecasts({ forecastId, token })
  }
)

export const loadAiForecast = createAsyncThunk(
  'aiForecast',
  async ({
    forecastId,
    favoriteSpotIds,
    token,
  }: {
    forecastId: string
    favoriteSpotIds: number[]
    token: string
  }) => {
    return api.getAiForecast({ forecastId, favoriteSpotIds, token })
  }
)

export const loadSpots = createAsyncThunk(
  'loadSpots',
  async ({ area, token }: { area: Area; token: string }) => {
    const spotIds = area.spots.map((spot) => spot.spotId)
    const liveWind = await api.getLiveWind({ spotIds, token })
    return area.spots.map((spot) => ({
      ...spot,
      liveWind: liveWind[spot.spotId],
    }))
  }
)

export const login = createAsyncThunk(
  'login',
  async ({ username, password }: { username: string; password: string }) => {
    return api.login(username, password)
  }
)

export const slice = createSlice({
  name: 'app',
  initialState: defaultState,
  reducers: {
    resetCache: (state) => ({ ...defaultState, token: state.token }),
    invalidToken: (state) => {
      localStorage.removeItem('token')
      return { ...state, token: null }
    },
  },
  extraReducers: (builder) => {
    // Load Spots
    builder.addCase(loadSpots.pending, (state) => {
      state.isLoadingSpotsWithData = true
    })
    builder.addCase(loadSpots.fulfilled, (state, action) => {
      state.loadCount += 1
      state.isLoadingSpotsWithData = false
      state.spotsWithData = action.payload
      state.lastUpdateTime = DateTime.now().toISO()
    })
    builder.addCase(loadSpots.rejected, (state, action) => {
      state.isLoadingSpotsWithData = false
    })

    // Load Forecast
    builder.addCase(loadForecasts.pending, (state) => state)
    builder.addCase(loadForecasts.fulfilled, (state, action) => {
      state.forecasts = action.payload
    })
    builder.addCase(loadForecasts.rejected, (state) => state)

    // Load AI Forecast
    builder.addCase(loadAiForecast.pending, (state) => {
      state.isLoadingAiForecast = true
      return state
    })
    builder.addCase(loadAiForecast.fulfilled, (state, action) => {
      state.aiForecast = action.payload
      state.isLoadingAiForecast = false
    })
    builder.addCase(loadAiForecast.rejected, (state) => {
      state.isLoadingAiForecast = false
    })

    // Login
    builder.addCase(login.pending, (state) => state)
    builder.addCase(login.fulfilled, (state, action) => {
      state.token = action.payload.token
      localStorage.setItem('token', action.payload.token)
    })
    builder.addCase(login.rejected, (state) => state)
  },
})

export const { resetCache, invalidToken } = slice.actions
export default slice.reducer
